import React, { useState } from 'react';
import { Button, Col, Form, InputGroup, Modal, Row, Tab, Tabs } from 'react-bootstrap';
import BzmIcon from '../../icons/BzmIcon';
import { ProcessingAction, Webhook, ActionMockDto } from '../../../types/rest/assetCatalogTypes';
import styles from './SandboxHttpActionModal.module.css';
import { TriggerTime } from '../../../views/AssetCatalog/Transactions/TransactionEditor/ProcessingActions/ProcessingActionModal';
import { getProcessingActionType } from '../../../views/AssetCatalog/Transactions/TransactionEditor/ProcessingActions/CommonActions';
import { PairSetItem } from '../../../components/pairSet/PairSetList';

interface SandboxHttpActionModalProps {
  processingAction: ProcessingAction;
  existingMock?: ActionMockDto;
  onClose: () => void;
  onSave: (mock: ActionMockDto) => void;
}

interface MockHeader {
  name: string;
  value: string;
}

const SandboxHttpActionModal = ({ processingAction, existingMock, onClose, onSave }: SandboxHttpActionModalProps) => {
  const definition = processingAction.definition as Webhook;
  const isWebhook = processingAction.actionType === 'WEBHOOK';

  const [statusCode, setStatusCode] = useState<string>(existingMock?.statusCode?.toString() ?? '200');
  const [mockHeaders, setMockHeaders] = useState<MockHeader[]>(existingMock?.headers ?? []);
  const [mockBody, setMockBody] = useState<string>(existingMock?.body ?? '');

  const addHeader = () => setMockHeaders(prev => [...prev, { name: '', value: '' }]);
  const removeHeader = (index: number) => setMockHeaders(prev => prev.filter((_, i) => i !== index));
  const updateHeader = (index: number, field: 'name' | 'value', value: string) => {
    setMockHeaders(prev => prev.map((h, i) => (i === index ? { ...h, [field]: value } : h)));
  };

  const handleSave = () => {
    const mock: ActionMockDto = {
      actionId: processingAction.id,
      actionName: processingAction.name,
      statusCode: parseInt(statusCode, 10) || undefined,
      headers: mockHeaders.filter(h => h.name),
      body: mockBody || undefined,
    };
    onSave(mock);
  };

  const renderPairs = (pairs: PairSetItem[]) => {
    if (!pairs || pairs.length === 0) {
      return <p className="text-muted small mt-2 mb-0">None</p>;
    }
    return (
      <table className="table table-sm mt-2 mb-0">
        <thead>
          <tr>
            <th style={{ borderTop: 'none' }}>Name</th>
            <th style={{ borderTop: 'none' }}>Value</th>
          </tr>
        </thead>
        <tbody>
          {pairs.map((p, i) => (
            <tr key={i}>
              <td>{p.name}</td>
              <td>{p.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <Modal
      show={true}
      onHide={onClose}
      enforceFocus={false}
      animation={false}
      backdropClassName={'backdrop-faded'}
      dialogClassName={styles.modalRight}
    >
      <Modal.Header closeButton>
        <h5 className="modal-title">
          {getProcessingActionType(processingAction.actionType)}: {processingAction.name}
        </h5>
      </Modal.Header>
      <Modal.Body>
        {/* Read-only action definition */}
        {isWebhook && (
          <Form.Group>
            <Form.Label className="font-weight-semi-bold">Trigger Time</Form.Label>
            <Form.Control
              disabled
              value={
                definition.triggerTime === TriggerTime.SPECIFIC
                  ? `Specific: ${definition.specific}ms`
                  : `Random: ${definition.lower}–${definition.upper}ms`
              }
            />
          </Form.Group>
        )}

        <Form.Group>
          <Form.Label className="font-weight-semi-bold">URL</Form.Label>
          <InputGroup>
            <InputGroup.Prepend>
              <InputGroup.Text>{definition.urlMethod}</InputGroup.Text>
            </InputGroup.Prepend>
            <Form.Control disabled value={definition.urlValue ?? ''} />
          </InputGroup>
        </Form.Group>

        <Tabs defaultActiveKey="queryParams" transition={false} id="sandbox-action-def-tabs">
          <Tab eventKey="queryParams" title="Query Parameters">
            {renderPairs(definition.queryParams)}
          </Tab>
          <Tab eventKey="headers" title="Headers">
            {renderPairs(definition.headers)}
          </Tab>
          <Tab eventKey="body" title="Body">
            <Form.Control as="textarea" rows={4} disabled value={definition.bodyContent ?? ''} className="mt-2" />
          </Tab>
        </Tabs>

        <hr className="mt-4" />

        {/* Sandbox Mock Response */}
        <h5 className="font-weight-semi-bold mb-3">Sandbox Response</h5>

        <Form.Group className="d-flex align-items-center mb-1">
          <Form.Label className="mb-0 mr-4">Status Code</Form.Label>
          <Form.Control
            type="number"
            value={statusCode}
            onChange={e => setStatusCode(e.target.value)}
            placeholder="e.g. 200"
            style={{ width: '60px' }}
          />
        </Form.Group>

        <Tabs defaultActiveKey="responseBody" transition={false} id="sandbox-action-mock-tabs">
          <Tab eventKey="responseHeaders" title="Response Headers">
            <div className="mt-2">
              {mockHeaders.map((header, index) => (
                <Row key={index} className="mb-3">
                  <Col xs={5}>
                    <Form.Group>
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        placeholder="Name"
                        value={header.name}
                        onChange={e => updateHeader(index, 'name', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={5}>
                    <Form.Group>
                      <Form.Label>Value</Form.Label>
                      <Form.Control
                        placeholder="Value"
                        value={header.value}
                        onChange={e => updateHeader(index, 'value', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={2} className="d-flex align-items-end pb-3">
                    <Button variant="danger" className="btn-circular" onClick={() => removeHeader(index)}>
                      <BzmIcon variant="icon-bzm-delete" />
                    </Button>
                    {index === mockHeaders.length - 1 && (
                      <Button variant="secondary" className="btn-circular ml-2" onClick={addHeader}>
                        <BzmIcon variant="icon-bzm-add" className="icon-theme-blue" />
                      </Button>
                    )}
                  </Col>
                </Row>
              ))}
              {mockHeaders.length === 0 && (
                <Button variant="primary" className="btn-circular" onClick={addHeader}>
                  <BzmIcon variant="icon-bzm-add" />
                </Button>
              )}
            </div>
          </Tab>
          <Tab eventKey="responseBody" title="Response Body">
            <Form.Control
              as="textarea"
              rows={4}
              value={mockBody}
              onChange={e => setMockBody(e.target.value)}
              placeholder="Response body..."
              className="mt-2"
            />
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave}>Save</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SandboxHttpActionModal;
